import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, Star, Users, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useTourDetails } from '@/hooks/useTourDetails';
import { useTranslation } from '@/hooks/useTranslation';
import { useSEO } from '@/hooks/useSEO';
import TourPlaceSection from '@/components/TourPlaceSection';
import { OptimizedImage } from '@/components/OptimizedImage';
import { getCanonicalUrl, getAlternateUrls, addHreflangLinks } from '@/lib/urlHelpers';
import { CriticalLoadingSpinner } from '@/components/CriticalLoadingSpinner';

const TourDetails: React.FC = () => {
  const { tourSlug } = useParams<{ tourSlug: string }>();
  const navigate = useNavigate();
  const { tour, loading, error } = useTourDetails(tourSlug);
  const { language, t } = useTranslation();

  const getStyleIcon = (style: string) => {
    switch (style) {
      case 'must-do': return <Star className="w-5 h-5" />;
      case 'best-rated': return <Users className="w-5 h-5" />;
      case 'off-the-beaten-path': return <Compass className="w-5 h-5" />;
      default: return <MapPin className="w-5 h-5" />;
    }
  };

  const getStyleLabel = (style: string) => {
    switch (style) {
      case 'must-do': return t('mustDo');
      case 'best-rated': return t('bestRated');
      case 'off-the-beaten-path': return t('offTheBeatenPath');
      default: return t('customTour');
    }
  };

  const getStyleDescription = (style: string) => {
    switch (style) {
      case 'must-do': return t('tourPageStyleMustDo');
      case 'best-rated': return t('tourPageStyleBestRated');
      case 'off-the-beaten-path': return t('tourPageStyleOffBeaten');
      default: return t('customTour');
    }
  };

  const getDurationLabel = (duration: string) => {
    if (duration === 'half-day') return t('tourPageHalfDay');
    if (duration === 'full-day') return t('tourPageFullDay');
    return duration;
  };

  // SEO content
  const durationText = tour ? getDurationLabel(tour.duration) : '';
  const styleDesc = tour ? getStyleDescription(tour.style) : '';
  
  const pageTitle = tour && language === 'FR'
    ? `Tour d'une ${durationText} de ${tour.city}`
    : tour ? `${durationText === 'half-day' ? 'Half day' : 'One day'} tour of ${tour.city}` : '';

  const metaDescription = tour && language === 'FR'
    ? `Découvrez notre tour d'une ${durationText} de ${tour.city} avec ${tour.places.length} lieux sélectionnés. Infos pratiques, podcasts culturels et conseils de visite inclus.`
    : tour ? `Discover our ${durationText} tour of ${tour.city} with ${tour.places.length} selected places. Practical info, cultural podcasts and visit tips included.` : '';

  const canonicalUrl = tourSlug ? getCanonicalUrl(`/tours/${tourSlug}`) : '';

  // Use SEO hook
  useSEO({
    title: pageTitle ? `${pageTitle} - DiskOver` : 'Tour - DiskOver',
    description: metaDescription,
    keywords: tour ? `${tour.city}, ${tour.country}, tour, travel guide, ${tour.style}, ${language === 'FR' ? 'voyage' : 'travel'}` : '',
    ogTitle: pageTitle,
    ogDescription: metaDescription,
    ogImage: tour?.image || undefined,
    canonical: canonicalUrl,
  });

  useEffect(() => {
    if (tourSlug) {
      const path = `/tours/${tourSlug}`;
      addHreflangLinks(path);
    }
  }, [tourSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <CriticalLoadingSpinner />
      </div>
    );
  }

  if (error || !tour) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
        <h1 className="text-2xl font-bold mb-4">{t('placeNotFound')}</h1>
        <p className="text-muted-foreground mb-6">{t('placeNotFoundDesc')}</p>
        <Button onClick={() => navigate('/tours')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('backButton')}
        </Button>
      </div>
    );
  }

  const subtitle = language === 'FR'
    ? `Que voir à ${tour.city} en une ${durationText} ? ${tour.places.length} lieux incontournables à découvrir.`
    : `What to see in ${tour.city} in ${durationText === 'half-day' ? 'half a day' : 'one day'}? ${tour.places.length} must-see places to discover.`;

  const introText = language === 'FR'
    ? `Avec notre équipe, nous avons créé un tour de ${tour.city} pour vous permettre de ${styleDesc}. Découvrez ci-dessous les lieux que vous ne devez pas manquer.`
    : `With our team, we created a tour of ${tour.city} to help you ${styleDesc}. Discover below the places you must not miss.`;

  const conclusionText = language === 'FR'
    ? "N'hésitez pas à sortir des chemins classiques et à explorer ! Faites-nous des retours si notre tour nécessite des améliorations."
    : "Don't hesitate to venture off the beaten path and explore! Send us your feedback if our tour needs improvements.";

  return (
    <>
      <main className="min-h-screen bg-background">
        {/* Hero Section with Image */}
        {tour.image && (
          <div className="relative w-full h-[40vh] md:h-[50vh] overflow-hidden">
            <OptimizedImage
              src={tour.image}
              alt={tour.city}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                  {pageTitle}
                </h1>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Badge className="bg-white/20 text-white backdrop-blur-sm border-none">
                    <MapPin className="w-4 h-4 mr-1" />
                    {tour.country}
                  </Badge>
                  <Badge className="bg-white/20 text-white backdrop-blur-sm border-none">
                    <Clock className="w-4 h-4 mr-1" />
                    {getDurationLabel(tour.duration)}
                  </Badge>
                  <Badge className="bg-white/20 text-white backdrop-blur-sm border-none">
                    {getStyleIcon(tour.style)}
                    <span className="ml-1">{getStyleLabel(tour.style)}</span>
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/tours')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backButton')}
          </Button>

          {/* Subtitle and Introduction */}
          <div className="mb-12 space-y-4">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {subtitle}
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {introText}
            </p>
          </div>

          <Separator className="my-12" />

          {/* Places List */}
          <div className="space-y-16 md:space-y-20">
            {tour.places.map((place, index) => (
              <React.Fragment key={place.name}>
                <TourPlaceSection
                  place={place}
                  index={index + 1}
                  language={language}
                />
                {index < tour.places.length - 1 && (
                  <Separator className="my-12" />
                )}
              </React.Fragment>
            ))}
          </div>

          <Separator className="my-12" />

          {/* Conclusion */}
          <div className="text-center py-8">
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {conclusionText}
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default TourDetails;
